import { DefaultAppTheme } from "@/themes/default.theme.ts";
import { AppThemeSidebar } from "@/themes/sidebar.ts";
import {
  BlueAppThemeConfig,
  DefaultAppThemeConfig,
  GreenAppThemeConfig,
  MaxAppThemeConfig,
  MidnightBlueAppThemeConfig,
  NeoAppThemeConfig,
  ElectricGreenAppThemeConfig,
  OrangeAppThemeConfig,
  PurpleAppThemeConfig,
  RedGrayAppThemeConfig,
  SkylineAppThemeConfig,
  VioletAppThemeConfig,
  VioletOpinionAppThemeConfig,
  WaveAppThemeConfig,
  YellowJacketAppThemeConfig,
  OpenSkyAppThemeConfig,
} from "@/themes/theme-app-configs.ts";
import { AppDistribution } from "@/types/common.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export interface AppTheme {
  body: AppThemeBase;
  layout: AppThemeLayout;
  sidebar: AppThemeSidebar;
}

export type AppThemeKey = keyof AppTheme;

export interface AppThemeConfig {
  name: string;

  colorBrandDark: string;
  colorBackgroundGradientFrom: string;
  colorBackgroundGradientTo: string;
  colorSidebar?: string;
}

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

function deepMergeTheme<T extends object>(base: T, override: DeepPartial<T>): T {
  const result = { ...base };
  for (const key of Object.keys(override) as (keyof T)[]) {
    const val = override[key];
    if (val !== undefined) {
      const baseVal = base[key];
      if (typeof val === "object" && !Array.isArray(val) && typeof baseVal === "object") {
        result[key] = deepMergeTheme(baseVal as object, val as DeepPartial<object>) as T[keyof T];
      } else {
        result[key] = val as T[keyof T];
      }
    }
  }
  return result;
}

export interface AppThemeConfigs {
  [key: string]: AppThemeConfig;
}

export interface AppThemeBundle {
  appTheme: AppTheme;
  appThemeConfig: AppThemeConfig;
}

interface AppThemeRegistration {
  appTheme?: DeepPartial<AppTheme>;
  appThemeConfig: AppThemeConfig;
}

export interface AppThemeBundleConfigs {
  [key: string]: AppThemeRegistration;
}

export const AppThemes: AppThemeBundleConfigs = {
  default: {
    appThemeConfig: DefaultAppThemeConfig,
  },
  green: {
    appThemeConfig: GreenAppThemeConfig,
  },
  neo: {
    appThemeConfig: NeoAppThemeConfig,
  },
  skyline: {
    appThemeConfig: SkylineAppThemeConfig,
  },
  max: {
    appThemeConfig: MaxAppThemeConfig,
  },
  wave: {
    appThemeConfig: WaveAppThemeConfig,
  },
  red_gray: {
    appThemeConfig: RedGrayAppThemeConfig,
  },
  blue: {
    appThemeConfig: BlueAppThemeConfig,
  },
  purple: {
    appThemeConfig: PurpleAppThemeConfig,
  },
  midnight_blue: {
    appThemeConfig: MidnightBlueAppThemeConfig,
  },
  violet: {
    appThemeConfig: VioletAppThemeConfig,
  },
  violet_opinion: {
    appThemeConfig: VioletOpinionAppThemeConfig,
  },
  orange: {
    appThemeConfig: OrangeAppThemeConfig,
  },
  electric_green: {
    appThemeConfig: ElectricGreenAppThemeConfig,
  },
  open_sky: { appThemeConfig: OpenSkyAppThemeConfig },
  yellow_jacket: {
    appTheme: {
      sidebar: {
        menu: {
          base: "bg-[hsl(var(--var-color-sidebar))]",
        },
      },
    },
    appThemeConfig: YellowJacketAppThemeConfig,
  },
};

export type AppThemesKeys = keyof typeof AppThemes;

export const getAppTheme = (theme: AppThemesKeys | null): AppThemeBundle => {
  const key =
    theme ??
    (import.meta.env.VITE_APP_DISTRIBUTION == AppDistribution.headly ? "violet" : "default");
  const registration = AppThemes[key] ?? AppThemes["default"];
  const appTheme = registration.appTheme
    ? deepMergeTheme(DefaultAppTheme, registration.appTheme)
    : DefaultAppTheme;
  return { appTheme, appThemeConfig: registration.appThemeConfig };
};

export interface AppThemeBase {
  base: string;
}

export interface AppThemeLayout extends AppThemeBase {
  main: string;
}

export type AppThemePage = AppThemeBase;

export interface AppPageTheme extends AppThemePage {
  content: string;
}

export const AvailableThemes = [
  { id: "default", title: `Original ${import.meta.env.VITE_APP_NAME}` },
  { id: "green", title: "Emerald green" },
  { id: "red_gray", title: "Red metal" },
  { id: "purple", title: "Fantasy violet" },
  { id: "blue", title: "Modern blue" },
  { id: "wave", title: "Sea wave" },
  { id: "max", title: "Sunset" },
  { id: "skyline", title: "CMYK" },
  { id: "neo", title: "Hoth blue" },
  { id: "midnight_blue", title: "Midnight blue" },
  { id: "violet", title: "Violet" },
  { id: "violet_opinion", title: "Violet Opinion" },
  { id: "orange", title: "Orange" },
  { id: "electric_green", title: "Electric Green" },
  { id: "yellow_jacket", title: "Yellow Jacket" },
  { id: "open_sky", title: "Open Sky" },
] as SelectItem[];
